import React, { useEffect, useRef, useState, useCallback } from "react";
import { ArrowLeft, MapPin, Check, Loader2 } from "lucide-react";
import { loadGoogleMaps, isGoogleMapsConfigured } from "../lib/googleMaps";

/**
 * Full-screen map picker overlay. Lets the user click/drag a pin on a Google Map,
 * reverse-geocodes the picked location, and returns a structured address.
 *
 * Returns via onConfirm({
 *   formatted_address: string,
 *   lat: number,
 *   lng: number,
 *   place_id: string|null,
 *   state: string,           // qark
 *   municipality: string,    // bashkia
 *   locality: string,
 *   postal_code: string,
 *   street: string,
 * })
 */

// Tirana city centre as the FALLBACK starting point when geolocation is denied/unavailable.
const FALLBACK_CENTER = { lat: 41.3275, lng: 19.8187 };
const DEFAULT_ZOOM = 13;
const GEOLOCATION_TIMEOUT_MS = 6000;

// Rough bounding box of Albania — used to ignore GPS positions from abroad.
const isWithinAlbania = ({ lat, lng }) =>
    lat >= 39.5 && lat <= 42.8 && lng >= 19.0 && lng <= 21.2;

// We only deliver inside Albania; the confirm button is blocked for foreign picks.
const isAlbaniaCountry = (c) => {
    const n = (c || "").trim().toLowerCase();
    return !n || ["albania", "shqipëria", "shqiperia", "shqipëri", "shqiperi"].includes(n);
};

/**
 * Ask the browser for the user's current position. Resolves to {lat, lng} on success
 * or `null` on permission denial / timeout / unsupported.
 */
const getCurrentPosition = () => new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
        resolve(null);
        return;
    }
    navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: 60000 },
    );
});

const extractComponents = (place) => {
    const result = {
        street: "",
        locality: "",
        municipality: "",
        state: "",
        postal_code: "",
        country: "",
    };
    const comps = place?.address_components || [];
    for (const c of comps) {
        const types = c.types || [];
        if (types.includes("route")) result.street = c.long_name;
        else if (types.includes("street_number")) result.street = (result.street ? `${c.long_name} ${result.street}` : c.long_name);
        else if (types.includes("sublocality") || types.includes("sublocality_level_1") || types.includes("neighborhood")) {
            result.locality = c.long_name;
        }
        else if (types.includes("locality")) result.municipality = c.long_name;
        else if (types.includes("administrative_area_level_2") && !result.municipality) result.municipality = c.long_name;
        else if (types.includes("administrative_area_level_1")) result.state = c.long_name;
        else if (types.includes("postal_code")) result.postal_code = c.long_name;
        else if (types.includes("country")) result.country = c.long_name;
    }
    return result;
};

const MapAddressPicker = ({ open, onClose, onConfirm, initial }) => {
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const geocoderRef = useRef(null);
    const searchRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [resolving, setResolving] = useState(false);
    const [picked, setPicked] = useState(null); // { lat, lng, formatted_address, components }
    const [error, setError] = useState(null);

    const reverseGeocode = useCallback((lat, lng) => {
        if (!geocoderRef.current) return;
        setResolving(true);
        geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
            setResolving(false);
            if (status !== "OK" || !results || !results.length) {
                setError("Couldn't resolve this location. Pick a nearby point.");
                return;
            }
            setError(null);
            const place = results[0];
            const components = extractComponents(place);
            setPicked({
                lat,
                lng,
                formatted_address: place.formatted_address || "",
                place_id: place.place_id,
                ...components,
            });
        });
    }, []);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        if (!isGoogleMapsConfigured()) {
            queueMicrotask(() => {
                if (cancelled) return;
                setError("Maps are not configured. Please use Manual mode.");
                setLoading(false);
            });
            return () => { cancelled = true; };
        }

        // Decide the starting centre:
        //   1. If the user already had a pin from a previous session/picker open, reuse it.
        //   2. Otherwise ask the browser for the current GPS position (HTTPS only).
        //      We only honour GPS when it's INSIDE Albania — we deliver only there, so a
        //      user browsing from abroad still starts on Tirana instead of their real city.
        //   3. If denied / unavailable / timed out / abroad, fall back to Tirana.
        const resolveStartCenter = async () => {
            if (initial?.lat && initial?.lng) {
                return { center: { lat: initial.lat, lng: initial.lng }, fromGPS: false };
            }
            const gps = await getCurrentPosition();
            if (gps && isWithinAlbania(gps)) return { center: gps, fromGPS: true };
            return { center: FALLBACK_CENTER, fromGPS: false };
        };

        Promise.all([loadGoogleMaps(), resolveStartCenter()]).then(([maps, { center: startCenter, fromGPS }]) => {
            if (cancelled || !mapRef.current) return;
            const map = new maps.Map(mapRef.current, {
                center: startCenter,
                zoom: fromGPS ? 16 : DEFAULT_ZOOM, // tighter zoom when we know exactly where the user is
                disableDefaultUI: true,
                zoomControl: true,
                gestureHandling: "greedy",
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                clickableIcons: false,
            });
            const marker = new maps.Marker({
                position: startCenter,
                map,
                draggable: true,
                animation: maps.Animation.DROP,
            });
            markerRef.current = marker;
            geocoderRef.current = new maps.Geocoder();

            // Move marker on map click
            map.addListener("click", (e) => {
                marker.setPosition(e.latLng);
                reverseGeocode(e.latLng.lat(), e.latLng.lng());
            });
            // Reverse-geocode on drag-end
            marker.addListener("dragend", () => {
                const p = marker.getPosition();
                reverseGeocode(p.lat(), p.lng());
            });

            // Search box
            if (searchRef.current) {
                const ac = new maps.places.Autocomplete(searchRef.current, {
                    types: ["geocode"],
                    componentRestrictions: { country: ["al"] },
                    fields: ["formatted_address", "geometry", "place_id", "address_components"],
                });
                ac.bindTo("bounds", map);
                ac.addListener("place_changed", () => {
                    const place = ac.getPlace();
                    if (!place.geometry?.location) return;
                    const loc = place.geometry.location;
                    map.panTo(loc);
                    map.setZoom(16);
                    marker.setPosition(loc);
                    const components = extractComponents(place);
                    setError(null);
                    setPicked({
                        lat: loc.lat(),
                        lng: loc.lng(),
                        formatted_address: place.formatted_address || "",
                        place_id: place.place_id,
                        ...components,
                    });
                });
            }

            // Trigger initial reverse-geocode so the user has a starting choice
            reverseGeocode(startCenter.lat, startCenter.lng);
            setLoading(false);
        }).catch((e) => {
            setError(`Could not load Google Maps: ${e.message || e}`);
            setLoading(false);
        });

        return () => {
            cancelled = true;
            markerRef.current = null;
            geocoderRef.current = null;
        };
    }, [open, initial, reverseGeocode]);

    if (!open) return null;

    return (
        <div
            data-testid="map-address-picker"
            className="fixed inset-0 z-[200] bg-white flex flex-col"
        >
            <header className="px-4 py-3 border-b border-neutral-200 flex items-center gap-3 bg-white shadow-sm shrink-0">
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="map-picker-back"
                    className="h-9 w-9 rounded-lg hover:bg-neutral-100 flex items-center justify-center"
                    aria-label="Back"
                >
                    <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">
                        Pick delivery location
                    </div>
                    <input
                        ref={searchRef}
                        type="search"
                        placeholder="Search a place in Albania…"
                        data-testid="map-picker-search"
                        className="w-full mt-0.5 text-sm bg-transparent outline-none placeholder:text-neutral-400"
                    />
                </div>
            </header>

            <div className="flex-1 relative">
                {loading && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-neutral-100 gap-2">
                        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
                        <p className="text-xs text-neutral-500">Locating you…</p>
                    </div>
                )}
                <div ref={mapRef} className="absolute inset-0" data-testid="map-picker-map" />
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-md text-xs font-semibold text-neutral-700 inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-[#FF5722]" />
                    Tap the map or drag the pin
                </div>
            </div>

            <footer className="border-t border-neutral-200 bg-white p-4 shrink-0">
                {error && (
                    <div className="text-xs text-rose-600 mb-2">{error}</div>
                )}
                {picked && !error && !isAlbaniaCountry(picked.country) && (
                    <div
                        data-testid="map-picker-outside-albania"
                        className="mb-2 p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700 font-semibold"
                    >
                        We deliver only within Albania. Please pick a location in Albania.
                    </div>
                )}
                {picked && !error && (
                    <div
                        data-testid="map-picker-preview"
                        className="mb-3 p-3 rounded-xl bg-neutral-50 border border-neutral-200 text-xs"
                    >
                        <div className="font-semibold text-neutral-900">
                            {picked.formatted_address || `${picked.lat.toFixed(5)}, ${picked.lng.toFixed(5)}`}
                        </div>
                        <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-neutral-600">
                            {picked.state && <div><span className="text-neutral-400">Qark:</span> {picked.state}</div>}
                            {picked.municipality && <div><span className="text-neutral-400">Bashkia:</span> {picked.municipality}</div>}
                            {picked.locality && <div><span className="text-neutral-400">Lagjja:</span> {picked.locality}</div>}
                            {picked.postal_code && <div><span className="text-neutral-400">PIN:</span> {picked.postal_code}</div>}
                            {picked.street && <div className="col-span-2"><span className="text-neutral-400">Rruga:</span> {picked.street}</div>}
                        </div>
                    </div>
                )}
                <button
                    type="button"
                    disabled={!picked || resolving || !isAlbaniaCountry(picked?.country)}
                    onClick={() => onConfirm(picked)}
                    data-testid="map-picker-confirm"
                    className="w-full h-12 rounded-xl bg-[#FF5722] hover:bg-[#E64A19] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                >
                    {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Use this location
                </button>
            </footer>
        </div>
    );
};

export default MapAddressPicker;
